import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { cva, VariantProps } from 'class-variance-authority'
import React from 'react'

type LiveViewStatusBadgeProps = Readonly<
  {
    status: LiveViewStatus
  } & React.ButtonHTMLAttributes<HTMLButtonElement>
>

const LiveViewStatusBadge = React.forwardRef<HTMLButtonElement, LiveViewStatusBadgeProps>(
  ({ status, className, ...props }, ref) => (
    <Button
      ref={ref}
      unstyled
      className={cn(liveViewStatusBadgeVariants({ status }), 'group/lvs', className)}
      {...props}
    >
      {LIVE_VIEW_STATUS_TEXT_MAP[status]}
      <div className='flex size-3.5 shrink-0 items-center justify-center'>
        <div className='flex size-3 items-center justify-center rounded-full bg-white outline outline-1 outline-white/50 transition-all duration-75 ease-in-out group-hover/lvs:size-[11px] group-hover/lvs:outline-[3px] group-focus/lvs:size-[11px] group-focus/lvs:outline-[3px] group-focus-visible/lvs:size-[11px] group-focus-visible/lvs:outline-[3px] group-active/lvs:size-[11px] group-active/lvs:outline-[3px]'>
          <PlayIcon className='ml-px shrink-0' />
        </div>
      </div>
    </Button>
  ),
)
LiveViewStatusBadge.displayName = 'LiveViewStatusBadge'

const LIVE_VIEW_STATUS_TEXT_MAP: Record<LiveViewStatus, string> = {
  recorded: 'Recorded',
  live: 'Live View',
  paused: 'Paused',
  actionRequired: 'Action Required',
}

type LiveViewStatus = NonNullable<VariantProps<typeof liveViewStatusBadgeVariants>['status']>

const liveViewStatusBadgeVariants = cva(
  'rounded-l-[3px] shrink-0 whitespace-nowrap rounded-r-2xl pl-1.5 pr-[3px] h-5 gap-2 text-[11px] leading-[11px] font-medium uppercase flex items-center justify-between text-white active:scale-[0.98] transition-transform duration-75 ease-in-out',
  {
    variants: {
      status: {
        recorded: 'bg-blue-200 w-[92px]',
        live: 'bg-green-200 w-[92px]',
        paused: 'bg-yellow-200 w-[92px]',
        actionRequired: 'bg-red-200',
      },
    },
  },
)

const PlayIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    width='5'
    height='6'
    viewBox='0 0 5 6'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    {...props}
  >
    <path
      d='M0.5 5V1C0.5 0.587977 0.970382 0.352786 1.3 0.6L3.96667 2.6C4.23333 2.8 4.23333 3.2 3.96667 3.4L1.3 5.4C0.970382 5.64721 0.5 5.41202 0.5 5Z'
      stroke='#827C8C'
      strokeLinecap='round'
    />
  </svg>
)

export { LiveViewStatusBadge, type LiveViewStatus }
